/**
 * ISO-8601 week keying for the weekly agent leaderboard — pure, tz-correct,
 * DST-safe, side-effect-free (per the `packages/shared` rules, the whole matrix
 * is unit-tested rather than exercised through Firestore).
 *
 * The leaderboard buckets each operator signup by the ISO week it happened in,
 * evaluated in the brand's market timezone. This mirrors the tz-local calendar
 * approach the streak mechanic (`loyalty/streak.ts`) and the daily rollover
 * (`aggregations/rollover.ts`) already use: resolve the tz-LOCAL calendar date
 * first (via `zonedDateISO`), then do pure calendar arithmetic for the ISO week
 * number. Because the instant is reduced to a local calendar date before any
 * week math, DST shifts (a 23- or 25-hour week) and year boundaries can't skew
 * the bucket.
 */

import { parseDateISO, zonedDateISO } from '../utils/tz.js';

/**
 * The brand market timezone the leaderboard weeks are evaluated in. Kilat is
 * Malaysia-first (ADR-0017); `Asia/Kuala_Lumpur` is a fixed +8 zone with no DST,
 * so this agrees day-for-day with the `Asia/Singapore` used by the daily
 * rollover — the constant is single-sourced here so the trigger and its tests
 * can never disagree about which week a signup lands in.
 */
export const LEADERBOARD_TZ = 'Asia/Kuala_Lumpur';

const MS_PER_DAY = 86_400_000;

/**
 * ISO-8601 week key (`YYYY-Www`, e.g. `2026-W30`) for the tz-local calendar week
 * containing `atMs`.
 *
 * ISO weeks run Monday→Sunday; week 1 is the week that contains the year's first
 * Thursday (equivalently, the week containing Jan 4). The week-numbering year in
 * the returned key can differ from the calendar year at a boundary — e.g. an
 * instant on 2021-01-01 (a Friday) belongs to `2020-W53`, and one on 2023-01-01
 * (a Sunday) belongs to `2022-W52`.
 *
 * @param atMs epoch milliseconds of the instant to bucket.
 * @param tz   IANA timezone the local calendar week is evaluated in.
 */
export function isoWeekKey(atMs: number, tz: string): string {
  // 1. Resolve the tz-LOCAL calendar date. From here it is pure UTC calendar
  //    arithmetic on that date — the `Date.UTC(...)` instants below are only
  //    stable day counters, so DST never re-enters.
  const { year, month, day } = parseDateISO(zonedDateISO(atMs, tz));
  const date = new Date(Date.UTC(year, month - 1, day));

  // 2. ISO weekday: Mon=1 … Sun=7 (JS `getUTCDay()` gives Sun=0 … Sat=6).
  const isoDow = date.getUTCDay() === 0 ? 7 : date.getUTCDay();

  // 3. Shift to the Thursday of THIS ISO week. That Thursday's calendar year IS
  //    the ISO week-numbering year (which is what can differ from `year`).
  date.setUTCDate(date.getUTCDate() + 4 - isoDow);
  const isoYear = date.getUTCFullYear();

  // 4. Week number from the Thursday's day-offset past Jan 1 of the ISO year.
  //    `floor(diffDays / 7) + 1` is the integer form of the canonical
  //    `ceil((diffDays + 1) / 7)` (equal for every diffDays ≥ 0, and the
  //    Thursday is never before Jan 1 of its own year). `Math.round` on the
  //    day-count first absorbs any floating-point drift — the two anchors are
  //    always an exact whole number of days apart.
  const jan1 = Date.UTC(isoYear, 0, 1);
  const diffDays = Math.round((date.getTime() - jan1) / MS_PER_DAY);
  const week = Math.floor(diffDays / 7) + 1;

  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
